/* global EmblaCarousel */

const ProductShowcase = (function ProductShowcaseModule() {
  const MOBILE_BREAKPOINT = 768; // hint only where slides are actually swipeable
  const emblaInstances = new Map(); // DOM panel element → emblaApi
  const controllers = []; // AbortControllers for cleanup
  const observers = []; // IntersectionObservers for cleanup

  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  // Play a one-time nudge on the visible carousel to hint horizontal swiping.
  function playSwipeHint(section) {
    const activePanel = [...section.querySelectorAll('[data-showcase-panel]')]
      .find((panel) => !panel.classList.contains('hidden'));
    const container = activePanel?.querySelector('.embla__container');
    if (!container) return;

    container.classList.add('animate-swipe-hint');
    container.addEventListener(
      'animationend',
      () => container.classList.remove('animate-swipe-hint'),
      { once: true },
    );
  }

  function observeForSwipeHint(section) {
    if (!('IntersectionObserver' in window)) return;

    const carousel = section.querySelector('[data-showcase-carousel]');
    if (!carousel) return;

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        obs.disconnect(); // one-time hint per section
        if (isMobile()) playSwipeHint(section);
      });
    }, { threshold: 0.6 });

    observer.observe(carousel);
    observers.push(observer);
  }

  function initEmblaForPanel(panel) {
    if (emblaInstances.has(panel)) return emblaInstances.get(panel);

    const viewport = panel.querySelector('[data-showcase-carousel]');
    if (!viewport || typeof EmblaCarousel === 'undefined') return null;

    const api = EmblaCarousel(viewport, {
      align: 'start',
      loop: false,
      dragFree: true,
    });

    const prevBtn = panel.querySelector('[data-showcase-prev]');
    const nextBtn = panel.querySelector('[data-showcase-next]');

    const updateButtons = () => {
      if (prevBtn) prevBtn.disabled = !api.canScrollPrev();
      if (nextBtn) nextBtn.disabled = !api.canScrollNext();
    };

    api.on('select', updateButtons);
    api.on('init', updateButtons);

    if (prevBtn) prevBtn.addEventListener('click', () => api.scrollPrev());
    if (nextBtn) nextBtn.addEventListener('click', () => api.scrollNext());

    updateButtons();
    emblaInstances.set(panel, api);
    return api;
  }

  function activateTab(allTabs, allPanels, targetIndex) {
    allTabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', i === targetIndex ? 'true' : 'false');
    });

    allPanels.forEach((panel, i) => {
      const isActive = i === targetIndex;
      panel.classList.toggle('hidden', !isActive);

      if (isActive) initEmblaForPanel(panel);
    });
  }

  function initSection(section, controller) {
    const tabsContainer = section.querySelector('[data-showcase-tabs]');
    if (!tabsContainer) return;

    const tabs = [...tabsContainer.querySelectorAll('[data-showcase-tab]')];
    const panels = [...section.querySelectorAll('[data-showcase-panel]')];
    if (!tabs.length) return;

    const opts = { signal: controller.signal };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const targetIndex = parseInt(tab.dataset.showcaseTab, 10);
        if (Number.isNaN(targetIndex)) return;
        activateTab(tabs, panels, targetIndex);
      }, opts);
    });

    // Keyboard navigation: ArrowLeft/Right/Home/End
    tabsContainer.addEventListener('keydown', (e) => {
      const focused = document.activeElement;
      const currentIndex = tabs.indexOf(focused);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;
      if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') nextIndex = 0;
      else if (e.key === 'End') nextIndex = tabs.length - 1;
      else return;

      e.preventDefault();
      tabs[nextIndex].focus();
      activateTab(tabs, panels, nextIndex);
    }, opts);

    // Activate first tab
    activateTab(tabs, panels, 0);

    // Nudge the carousel once when it first scrolls into view (mobile only)
    observeForSwipeHint(section);
  }

  function init() {
    document.querySelectorAll('[data-product-showcase]').forEach((section) => {
      const controller = new AbortController();
      controllers.push(controller);
      initSection(section, controller);
    });
  }

  function destroy() {
    controllers.forEach((c) => c.abort());
    controllers.length = 0;
    observers.forEach((o) => o.disconnect());
    observers.length = 0;
    emblaInstances.forEach((api) => api.destroy());
    emblaInstances.clear();
  }

  return { init, destroy };
})();

document.addEventListener('DOMContentLoaded', ProductShowcase.init);
